import { IconExclamationCircle, type Icon } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { useId, useMemo, type ComponentProps, type FC } from 'react'

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
  HStack,
  VStack
} from '@/components'

export interface FormInputProps extends ComponentProps<typeof FormControl> {
  error?: string
  errorIcon?: Icon
  errorIconProps?: Omit<ComponentProps<typeof FormControlErrorIcon>, 'as' | 'children'>
  errorProps?: Omit<ComponentProps<typeof FormControlError>, 'children'>
  errorTextProps?: Omit<ComponentProps<typeof FormControlErrorText>, 'children'>
  helper?: string
  helperProps?: Omit<ComponentProps<typeof FormControlHelper>, 'children'>
  helperTextProps?: Omit<ComponentProps<typeof FormControlHelperText>, 'children'>
  label?: string
  labelProps?: Omit<ComponentProps<typeof FormControlLabel>, 'children'>
  labelTextProps?: Omit<ComponentProps<typeof FormControlLabelText>, 'children'>
  withInlineLabel?: boolean
}

export const FormInput: FC<FormInputProps> = ({
  children,
  className,
  error,
  errorIcon = IconExclamationCircle,
  errorIconProps,
  errorProps,
  errorTextProps,
  helper: _helper,
  helperProps,
  helperTextProps,
  label,
  labelProps,
  labelTextProps,
  nativeID: _nativeID,
  size,
  withInlineLabel,
  ...controlProps
}) => {
  const defaultId = useId()
  const nativeID = _nativeID ?? defaultId
  const labelElement = useMemo(
    () => (
      <FormControlLabel
        accessibilityElementsHidden
        aria-hidden
        nativeID={`${nativeID}-label`}
        importantForAccessibility='no-hide-descendants'
        className='flex-shrink'
        {...labelProps}
      >
        <FormControlLabelText
          className='text-white'
          {...labelTextProps}
        >
          {label}
        </FormControlLabelText>
      </FormControlLabel>
    ),
    [nativeID, label, labelProps, labelTextProps]
  )
  const helper = useMemo(
    () =>
      _helper ? (
        <FormControlHelper
          className='mb-1'
          nativeID={`${nativeID}-helper`}
          {...helperProps}
        >
          <FormControlHelperText
            accessibilityElementsHidden
            aria-hidden
            importantForAccessibility='no-hide-descendants'
            className='text-white'
            {...helperTextProps}
          >
            {_helper}
          </FormControlHelperText>
        </FormControlHelper>
      ) : null,
    [_helper, helperProps, helperTextProps, nativeID]
  )

  return (
    <FormControl
      className={clsx('gap-1', className)}
      size={size}
      {...controlProps}
    >
      {withInlineLabel ? (
        <HStack className='items-start justify-between gap-2'>
          <VStack className='shrink gap-1'>
            {labelElement}
            {helper}
          </VStack>
          {children}
        </HStack>
      ) : (
        <>
          {labelElement}
          {helper}
          {children}
        </>
      )}
      {error ? (
        <FormControlError {...errorProps}>
          <FormControlErrorIcon
            accessibilityElementsHidden
            aria-hidden
            as={errorIcon}
            importantForAccessibility='no-hide-descendants'
            {...errorIconProps}
          />
          <FormControlErrorText
            accessibilityLiveRegion='assertive'
            accessibilityRole='alert'
            className='text-brand-orange-500'
            nativeID={`${nativeID}-error`}
            {...errorTextProps}
          >
            {error}
          </FormControlErrorText>
        </FormControlError>
      ) : null}
    </FormControl>
  )
}
